import React, { useEffect, useMemo, useState, useRef, createElement } from 'react';
import { PlusIcon, FilterIcon, SearchIcon, CheckCircleIcon, ClockIcon, AlertCircleIcon, FilterXIcon, XIcon, PencilIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { AddTaskModal } from '../AddTaskModal';
import { TaskDetailsPanel } from '../tasks/TaskDetailsPanel';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../providers/ThemeProvider';
import { getTaskDisplayStatus } from '../tasks/taskUtils';
import { getTeacherTasks, getTeacherTasksPaginated, addTask, updateTask, deleteTask } from '../../services/taskService';
import { toast } from 'react-hot-toast';
export const TeacherTasks = () => {
  const {
    currentUser,
    loading: authLoading
  } = useAuth();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [allTasks, setAllTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isTaskPanelOpen, setIsTaskPanelOpen] = useState(false);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  // Pagination state
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // State flag to trigger filter switch after task status update
  const [pendingFilterSwitch, setPendingFilterSwitch] = useState(false);
  // Reference for the chart
  const chartContainer = useRef(null);
  // Reference to the chart instance
  const chartInstance = useRef(null);
  // State to track which task notes are expanded
  const [expandedNoteIds, setExpandedNoteIds] = useState([]);
  // State to track fully expanded notes
  const [fullyExpandedNoteIds, setFullyExpandedNoteIds] = useState([]);
  // State to track chart filter
  const [chartFilter, setChartFilter] = useState(null);
  // NEW: Track the source of the filter (dropdown vs chart click)
  const [filterSource, setFilterSource] = useState<'dropdown' | 'chart' | null>(null);
  // Toggle note expansion
  const toggleNoteExpansion = (e, taskId) => {
    e.stopPropagation(); // Prevent opening the task details panel
    setExpandedNoteIds(prevIds => {
      // If this task is already expanded, collapse it
      if (prevIds.includes(taskId)) {
        return prevIds.filter(id => id !== taskId);
      }
      // Otherwise expand this task and collapse any others (optional requirement)
      return [taskId];
    });
    // When collapsing, also remove from fully expanded if present
    if (expandedNoteIds.includes(taskId)) {
      setFullyExpandedNoteIds(prevIds => prevIds.filter(id => id !== taskId));
    }
  };
  // Toggle full note expansion
  const toggleFullNoteExpansion = (e, taskId) => {
    e.stopPropagation(); // Prevent opening the task details panel
    setFullyExpandedNoteIds(prevIds => {
      if (prevIds.includes(taskId)) {
        return prevIds.filter(id => id !== taskId);
      }
      return [...prevIds, taskId];
    });
  };
  // Function to truncate notes text
  const truncateNotes = text => {
    if (!text || text.length <= 30) return text;
    const words = text.split(' ');
    if (words.length <= 4) return text;
    return words.slice(0, 4).join(' ') + '...';
  };
  // Load tasks from Firestore
  useEffect(() => {
    const fetchTasks = async () => {
      // CRITICAL FIX: Don't fetch if auth is still loading
      if (authLoading) {
        console.log('Auth still loading, skipping task fetch');
        return;
      }
      if (!currentUser?.uid) {
        console.log('No current user, skipping task fetch');
        setIsLoadingTasks(false);
        return;
      }
      setIsLoadingTasks(true);
      try {
        // Fetch from Firestore with pagination (initial load)
        const {
          tasks: firestoreTasks,
          lastDoc: newLastDoc,
          hasMore: moreAvailable
        } = await getTeacherTasksPaginated(currentUser.uid, null, 25);
        console.log('Loaded tasks from Firestore:', firestoreTasks.length);
        setAllTasks(firestoreTasks);
        setLastDoc(newLastDoc);
        setHasMore(moreAvailable);
        // Also sync to localStorage as backup
        const TASKS_STORAGE_KEY = 'mc_tasks_temp';
        localStorage.setItem(TASKS_STORAGE_KEY, JSON.stringify(firestoreTasks));
      } catch (error) {
        console.error('Error fetching tasks from Firestore:', error);
        toast.error('Failed to load tasks');
        // Fall back to localStorage if Firestore fails
        try {
          const storedTasks = localStorage.getItem('mc_tasks_temp');
          if (storedTasks) {
            const localTasks = JSON.parse(storedTasks);
            setAllTasks(localTasks);
            console.log('Loaded tasks from localStorage backup');
          }
        } catch (localError) {
          console.error('Error loading tasks from localStorage:', localError);
        }
      } finally {
        setIsLoadingTasks(false);
      }
    };
    // Only fetch when auth is ready
    if (!authLoading) {
      fetchTasks();
    }
    // Listen for task events to refresh
    const handleTaskEvent = () => {
      if (!authLoading) {
        fetchTasks();
      }
    };
    window.addEventListener('taskCreated', handleTaskEvent);
    window.addEventListener('taskUpdated', handleTaskEvent);
    window.addEventListener('taskDeleted', handleTaskEvent);
    return () => {
      window.removeEventListener('taskCreated', handleTaskEvent);
      window.removeEventListener('taskUpdated', handleTaskEvent);
      window.removeEventListener('taskDeleted', handleTaskEvent);
    };
  }, [currentUser?.uid, authLoading]);
  // Function to load more tasks
  const loadMoreTasks = async () => {
    if (!currentUser?.uid || !lastDoc || isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    try {
      const {
        tasks: newTasks,
        lastDoc: newLastDoc,
        hasMore: moreAvailable
      } = await getTeacherTasksPaginated(currentUser.uid, lastDoc, 25);
      if (newTasks.length > 0) {
        const updatedTasks = [...allTasks, ...newTasks];
        setAllTasks(updatedTasks);
        setLastDoc(newLastDoc);
        setHasMore(moreAvailable);
        // Update localStorage backup
        const TASKS_STORAGE_KEY = 'mc_tasks_temp';
        localStorage.setItem(TASKS_STORAGE_KEY, JSON.stringify(updatedTasks));
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading more tasks:', error);
      toast.error('Failed to load more tasks');
    } finally {
      setIsLoadingMore(false);
    }
  };
  // Derive filtered tasks from allTasks - ALWAYS filter from master list
  const filteredTasks = useMemo(() => {
    let filtered = [...allTasks];
    // CRITICAL: Always exclude completed tasks unless explicitly viewing them
    if (statusFilter !== 'completed') {
      filtered = filtered.filter(task => task.status !== 'completed');
    }
    // Apply search filter
    if (searchQuery) {
      const lowercaseQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(task => (task.title || task.name || '').toLowerCase().includes(lowercaseQuery) || (task.studentName || '').toLowerCase().includes(lowercaseQuery) || (task.notes || '').toLowerCase().includes(lowercaseQuery));
    }
    // Apply status filter for non-'all' and non-'completed' cases
    if (statusFilter !== 'all' && statusFilter !== 'completed') {
      // Use getTaskDisplayStatus for computed statuses (overdue, in-progress, pending)
      filtered = filtered.filter(task => {
        const displayStatus = getTaskDisplayStatus(task);
        return displayStatus === statusFilter;
      });
    }
    // If status === 'completed', show only completed tasks
    if (statusFilter === 'completed') {
      filtered = filtered.filter(task => task.status === 'completed');
    }
    // Sort by due date (ascending)
    filtered.sort((a, b) => {
      const dateA = new Date(a.deadline || a.dueDate);
      const dateB = new Date(b.deadline || b.dueDate);
      return dateA - dateB;
    });
    return filtered;
  }, [allTasks, searchQuery, statusFilter]);
  // React-idiomatic filter switch after task update
  useEffect(() => {
    if (pendingFilterSwitch) {
      // FIXED: Don't switch filter if currently viewing completed tasks
      // This prevents the filter from resetting when clicking completed tasks
      if (statusFilter !== 'completed') {
        // Switch to Active view after tasks state has updated
        setSearchQuery('');
        setStatusFilter('all');
        setChartFilter(null);
      }
      // Always clear the flag
      setPendingFilterSwitch(false);
    }
  }, [pendingFilterSwitch, statusFilter]);
  // Function to update chart with filtered data
  const updateChartWithFilteredData = filteredData => {
    if (chartInstance.current) {
      // Count tasks by computed status
      const taskStatusCounts = {
        completed: 0,
        'in-progress': 0,
        overdue: 0,
        pending: 0
      };
      filteredData.forEach(task => {
        const displayStatus = getTaskDisplayStatus(task);
        taskStatusCounts[displayStatus] += 1;
      });
      // Update chart data
      chartInstance.current.series[0].setData([{
        name: 'Completed',
        y: taskStatusCounts.completed,
        color: '#22c55e',
        status: 'completed' // Add status property
      }, {
        name: 'In Progress',
        y: taskStatusCounts['in-progress'],
        color: '#3b82f6',
        status: 'in-progress' // Add status property
      }, {
        name: 'Overdue',
        y: taskStatusCounts.overdue,
        color: '#ef4444',
        status: 'overdue' // Add status property
      }, {
        name: 'Pending',
        y: taskStatusCounts.pending,
        color: '#a855f7',
        status: 'pending' // Add status property
      }]);
    }
  };
  // Update chart when filtered tasks change
  useEffect(() => {
    updateChartWithFilteredData(filteredTasks);
  }, [filteredTasks]);
  // Function to open task details panel
  const openTaskDetails = task => {
    setSelectedTask(task);
    setIsTaskPanelOpen(true);
    setIsEditMode(false); // Always start in view mode
  };
  // Function to close task details panel
  const closeTaskDetails = () => {
    setIsTaskPanelOpen(false);
    setIsEditMode(false);
    setTimeout(() => {
      setSelectedTask(null);
    }, 300);
  };
  // Function to handle edit click
  const handleEditClick = () => {
    setIsEditMode(true);
  };
  // Function to handle exit edit mode
  const handleExitEditMode = () => {
    setIsEditMode(false);
  };
  // Function to handle task deletion
  const handleDeleteTask = async taskId => {
    if (!currentUser?.uid || !selectedTask?.studentId) {
      toast.error('Cannot delete task: missing information');
      return;
    }
    try {
      // Delete from Firestore
      await deleteTask(currentUser.uid, selectedTask.studentId, taskId);
      console.log('Task deleted from Firestore');
      // Remove from master list
      const updatedTasks = allTasks.filter(task => task.id !== taskId);
      setAllTasks(updatedTasks);
      // Update localStorage backup
      const TASKS_STORAGE_KEY = 'mc_tasks_temp';
      localStorage.setItem(TASKS_STORAGE_KEY, JSON.stringify(updatedTasks));
      // Dispatch event
      window.dispatchEvent(new Event('taskDeleted'));
      toast.success('Task deleted successfully');
      closeTaskDetails();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };
  // Function to toggle task completion status
  const handleToggleComplete = async (task, e) => {
    e.stopPropagation(); // Prevent opening task details panel
    if (!currentUser?.uid || !task.studentId) {
      toast.error('Cannot update task: missing information');
      return;
    }
    const isCurrentlyCompleted = task.status === 'completed';
    const newStatus = isCurrentlyCompleted ? 'pending' : 'completed';
    const completedAt = isCurrentlyCompleted ? null : new Date().toISOString();
    // Store the task data needed for undo (avoid closure issues)
    const taskId = task.id;
    const studentId = task.studentId;
    const previousStatus = task.status;
    const previousCompletedAt = task.completedAt;
    // Optimistic update - update master task list
    const updatedTasks = allTasks.map(t => t.id === task.id ? {
      ...t,
      status: newStatus,
      completedAt
    } : t);
    setAllTasks(updatedTasks);
    // React-idiomatic: Set flag to trigger filter switch after tasks update
    if (isCurrentlyCompleted) {
      setPendingFilterSwitch(true);
    }
    try {
      // Update in Firestore
      await updateTask(currentUser.uid, task.studentId, task.id, {
        status: newStatus,
        completedAt
      });
      console.log('Task completion status updated in Firestore');
      // Update localStorage backup
      const TASKS_STORAGE_KEY = 'mc_tasks_temp';
      localStorage.setItem(TASKS_STORAGE_KEY, JSON.stringify(updatedTasks));
      // Dispatch event to notify other components (Dashboard)
      window.dispatchEvent(new Event('taskUpdated'));
      // Show undo toast ONLY when marking as completed
      if (newStatus === 'completed') {
        toast.success(t => <div className="flex items-center justify-between gap-3" data-id="element-10244">
              <span data-id="element-10245">Task marked as completed</span>
              <button onClick={async () => {
            toast.dismiss(t.id);
            // FIXED: Use functional state update to avoid stale closure
            setAllTasks(currentTasks => {
              return currentTasks.map(task_item => task_item.id === taskId ? {
                ...task_item,
                status: previousStatus,
                completedAt: previousCompletedAt
              } : task_item);
            });
            // Set flag to switch to Active view after undo
            setPendingFilterSwitch(true);
            try {
              // Update Firestore with reverted state
              await updateTask(currentUser.uid, studentId, taskId, {
                status: previousStatus,
                completedAt: previousCompletedAt
              });
              // Update localStorage with current state
              setAllTasks(currentTasks => {
                const TASKS_STORAGE_KEY = 'mc_tasks_temp';
                localStorage.setItem(TASKS_STORAGE_KEY, JSON.stringify(currentTasks));
                return currentTasks;
              });
              // Dispatch event
              window.dispatchEvent(new Event('taskUpdated'));
              toast.success('Task restored');
            } catch (error) {
              console.error('Error undoing task completion:', error);
              toast.error('Failed to undo');
              // Revert back to completed state on error
              setAllTasks(currentTasks => {
                return currentTasks.map(task_item => task_item.id === taskId ? {
                  ...task_item,
                  status: 'completed',
                  completedAt
                } : task_item);
              });
            }
          }} className="px-3 py-1 bg-white text-gray-800 rounded-md font-medium hover:bg-gray-100 transition-colors" data-id="element-10246">
                Undo
              </button>
            </div>, {
          duration: 6000,
          style: {
            minWidth: '300px'
          }
        });
      } else {
        // Simple message when marking as incomplete
        toast.success('Task marked as incomplete');
      }
    } catch (error) {
      console.error('Error updating task completion:', error);
      // Revert optimistic update on error
      setAllTasks(allTasks);
      // Clear the pending filter switch flag on error
      setPendingFilterSwitch(false);
      toast.error('Failed to update task');
    }
  };
  // Function to handle saving edited task
  const handleSaveTask = async (taskId, updates) => {
    if (!currentUser?.uid || !selectedTask?.studentId) {
      toast.error('Cannot update task: missing information');
      return;
    }
    try {
      // Build update data object, only including dueDate if it has a value
      const updateData: any = {
        title: updates.title,
        status: updates.status,
        notes: updates.notes || '',
        reference: updates.reference || ''
      };
      // Only include dueDate if updates.dueAt has a value
      if (updates.dueAt) {
        updateData.dueDate = updates.dueAt;
      }
      // Include startDate if it has a value
      if (updates.startDate) {
        updateData.startDate = updates.startDate;
      }
      // Update in Firestore
      await updateTask(currentUser.uid, selectedTask.studentId, taskId, updateData);
      console.log('Task updated in Firestore');
      // Create a completely new task object with updated fields
      const updatedTaskData = {
        ...selectedTask,
        id: taskId,
        name: updates.title,
        title: updates.title,
        deadline: updates.dueAt || selectedTask.deadline,
        dueDate: updates.dueAt || selectedTask.dueDate,
        startDate: updates.startDate || selectedTask.startDate,
        notes: updates.notes || '',
        status: updates.status,
        reference: updates.reference || ''
      };
      // Update the task in the master list
      const updatedTasks = allTasks.map(task => {
        if (task.id === taskId) {
          return updatedTaskData;
        }
        return task;
      });
      // Update tasks state
      setAllTasks(updatedTasks);
      // CRITICAL: Update selectedTask with the exact same object reference
      // This ensures the panel receives the updated status
      setSelectedTask(updatedTaskData);
      // Update localStorage backup
      const TASKS_STORAGE_KEY = 'mc_tasks_temp';
      localStorage.setItem(TASKS_STORAGE_KEY, JSON.stringify(updatedTasks));
      // Dispatch event to notify other components
      window.dispatchEvent(new Event('taskUpdated'));
      toast.success('Task updated successfully');
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    }
  };
  // Function to handle successful task addition
  const handleTaskAddSuccess = async newTask => {
    if (!currentUser?.uid) {
      toast.error('Cannot create task: not logged in');
      return;
    }
    try {
      // Add to Firestore
      const taskId = await addTask(currentUser.uid, newTask.studentId, {
        title: newTask.title,
        studentId: newTask.studentId,
        studentName: newTask.studentName,
        dueDate: newTask.dueDate,
        startDate: newTask.startDate,
        notes: newTask.notes || '',
        status: newTask.status || 'pending',
        reference: newTask.reference || '',
        lessonId: newTask.lessonId || undefined
      });
      console.log('Task added to Firestore with ID:', taskId);
      // Add the new task to master list with Firestore ID
      const taskWithId = {
        id: taskId,
        ...newTask
      };
      const updatedTasks = [...allTasks, taskWithId];
      setAllTasks(updatedTasks);
      // Update localStorage backup
      const TASKS_STORAGE_KEY = 'mc_tasks_temp';
      localStorage.setItem(TASKS_STORAGE_KEY, JSON.stringify(updatedTasks));
      // Dispatch event
      window.dispatchEvent(new Event('taskCreated'));
      toast.success('Task created successfully!');
      setIsAddTaskModalOpen(false);
    } catch (err) {
      console.error('Error adding task:', err);
      toast.error('Failed to create task');
    }
  };
  // Format date for display
  const formatDate = dateStr => {
    if (!dateStr) return 'No date';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  // Get status badge class
  const getStatusBadgeClass = status => {
    switch (status) {
      case 'completed':
        return 'bg-[rgba(34,197,94,0.18)] border border-[rgba(34,197,94,0.45)] text-[#dff7e8]';
      case 'in-progress':
        return 'bg-[rgba(59,130,246,0.18)] border border-[rgba(59,130,246,0.45)] text-[#dbeafe]';
      default:
        return 'bg-[rgba(168,85,247,0.18)] border border-[rgba(168,85,247,0.45)] text-[#f3e8ff]';
    }
  };
  // Get status icon color
  const getStatusIconColor = status => {
    switch (status) {
      case 'completed':
        return '#22c55e';
      // green
      case 'in-progress':
        return '#3b82f6';
      // yellow
      default:
        return '#a855f7';
      // purple
    }
  };
  // Get task status class - UPDATED to use getTaskDisplayStatus utility
  const getTaskStatusClass = task => {
    const displayStatus = getTaskDisplayStatus(task);
    switch (displayStatus) {
      case 'completed':
        return 'status-completed';
      case 'overdue':
        return 'status-overdue';
      case 'in-progress':
        return 'status-inprogress';
      case 'pending':
      default:
        return 'status-pending';
    }
  };
  // Reset chart filter
  const resetChartFilter = () => {
    setChartFilter(null);
    setFilterSource(null);
    // Reset the dropdown filter to 'all' when clearing chart filter
    setStatusFilter('all');
  };
  // Handle document click to reset filter when clicking outside the chart
  useEffect(() => {
    const handleDocumentClick = e => {
      // FIXED: Only reset if filter was initiated by chart click, not dropdown
      if (!chartFilter || filterSource !== 'chart') return;
      // Check if click is outside the chart container
      if (chartContainer.current && !chartContainer.current.contains(e.target)) {
        // FIXED: Don't reset filter when clicking inside the task list area
        // This prevents completed tasks from disappearing when clicking on them
        const taskListArea = document.getElementById('content-left');
        if (taskListArea && taskListArea.contains(e.target as Node)) {
          return;
        }
        resetChartFilter();
      }
    };
    document.addEventListener('mousedown', handleDocumentClick);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [chartFilter, filterSource]);
  // Load Highcharts and initialize the donut chart
  useEffect(() => {
    // Count tasks by status - UPDATED to use getTaskDisplayStatus utility
    const taskStatusCounts = {
      completed: 0,
      'in-progress': 0,
      overdue: 0,
      pending: 0
    };
    // Count tasks by computed status from ALL tasks
    allTasks.forEach(task => {
      const displayStatus = getTaskDisplayStatus(task);
      taskStatusCounts[displayStatus] += 1;
    });
    // Load Highcharts scripts
    const loadHighcharts = () => {
      return new Promise(resolve => {
        // Check if Highcharts is already loaded
        if (window.Highcharts) {
          resolve();
          return;
        }
        // Load Highcharts core
        const script1 = document.createElement('script');
        script1.src = 'https://code.highcharts.com/highcharts.js';
        script1.async = true;
        document.body.appendChild(script1);
        script1.onload = () => {
          // Load Highcharts 3D module
          const script2 = document.createElement('script');
          script2.src = 'https://code.highcharts.com/highcharts-3d.js';
          script2.async = true;
          document.body.appendChild(script2);
          script2.onload = () => {
            resolve();
          };
        };
      });
    };
    // Initialize the chart
    const initChart = () => {
      if (!window.Highcharts || !chartContainer.current) return;
      // Check if chart already exists
      if (chartInstance.current) {
        // Update existing chart with new data
        updateChartWithFilteredData(allTasks);
        return;
      }
      // Initialize new chart
      chartInstance.current = window.Highcharts.chart('assigned-tasks-donut', {
        chart: {
          type: 'pie',
          backgroundColor: 'transparent',
          options3d: {
            enabled: true,
            alpha: 45,
            beta: 0
          },
          animation: true,
          height: 280
        },
        title: {
          text: null
        },
        credits: {
          enabled: false
        },
        tooltip: {
          pointFormat: '<b>{point.y}</b> ({point.percentage:.1f}%)'
        },
        plotOptions: {
          pie: {
            innerSize: 100,
            depth: 45,
            cursor: 'pointer',
            dataLabels: {
              enabled: true,
              distance: 12,
              format: '{point.name}',
              style: {
                textOutline: 'none',
                fontSize: '12px',
                fontWeight: '500',
                color: isDark ? '#fff' : '#333'
              }
            },
            point: {
              events: {
                click: function () {
                  // Get the status directly from the point's status property
                  const filterStatus = this.status;
                  // Update both filters to ensure consistency
                  setStatusFilter(filterStatus);
                  setChartFilter(filterStatus);
                  // NEW: Mark this as a chart-initiated filter
                  setFilterSource('chart');
                }
              }
            }
          },
          series: {
            animation: {
              duration: 450
            }
          }
        },
        series: [{
          name: 'Tasks',
          data: [{
            name: 'Completed',
            y: taskStatusCounts.completed,
            color: '#22c55e',
            status: 'completed' // Add status property
          }, {
            name: 'In Progress',
            y: taskStatusCounts['in-progress'],
            color: '#3b82f6',
            status: 'in-progress' // Add status property
          }, {
            name: 'Overdue',
            y: taskStatusCounts.overdue,
            color: '#ef4444',
            status: 'overdue' // Add status property
          }, {
            name: 'Pending',
            y: taskStatusCounts.pending,
            color: '#a855f7',
            status: 'pending' // Add status property
          }]
        }]
      });
    };
    // Load scripts and initialize chart
    loadHighcharts().then(() => {
      initChart();
    });
    // Cleanup function
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [allTasks, isDark]);
  // Filter tasks based on chart filter for display
  const displayTasks = filteredTasks;
  return <>
      {/* Status Color Variables */}
      <style jsx global data-id="element-10247">{`
        :root {
          --status-completed: #22c55e;
          --status-inprogress: #3b82f6;
          --status-overdue: #ef4444;
          --status-pending: #a855f7;
          --surface-1: rgba(255, 255, 255, 0.06);
          --text-strong: #e6ebf5;
          --text-muted: #a9b2c3;
          --notes-toggle-dark: #3b82f6;
          --notes-toggle-light: #3b82f6;
          --notes-toggle-hover-dark: #60a5fa;
          --notes-toggle-hover-light: #2563eb;
        }

        .task-row {
          transition:
            transform 0.2s ease,
            box-shadow 0.2s ease,
            border-color 0.2s ease,
            background-color 0.2s ease;
          border-radius: 10px;
          margin-bottom: 8px;
          will-change: transform;
          transform: translateZ(0);
          backface-visibility: hidden;
        }

        html:not(.dark) .task-row {
          border: 1.5px solid #9ca3af;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
          background-color: white;
        }

        html:not(.dark) .task-row:hover {
          transform: translateY(-2px);
          border-color: #6b7280;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        html.dark .task-row {
          border: 1.5px solid rgba(255, 255, 255, 0.25);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        }

        html.dark .task-row:hover {
          transform: translateY(-2px);
          background: var(--surface-1);
          border-color: rgba(255, 255, 255, 0.4);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
        }

        .status-dot {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          display: inline-block;
          flex-shrink: 0;
          box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.15) inset;
        }

        .status-completed .status-dot {
          background: var(--status-completed);
        }

        html.dark .status-completed .status-pill {
          color: #dff7e8;
          background: rgba(34, 197, 94, 0.18);
          border: 1px solid rgba(34, 197, 94, 0.45);
        }

        html:not(.dark) .status-completed .status-pill {
          color: #166534;
          background: #dcfce7;
          border: 1px solid rgba(34, 197, 94, 0.45);
        }

        .status-inprogress .status-dot {
          background: var(--status-inprogress);
        }

        html.dark .status-inprogress .status-pill {
          color: #dbeafe;
          background: rgba(59, 130, 246, 0.18);
          border: 1px solid rgba(59, 130, 246, 0.45);
        }

        html:not(.dark) .status-inprogress .status-pill {
          color: #1e3a8a;
          background: #dbeafe;
          border: 1px solid rgba(59, 130, 246, 0.45);
        }

        .status-overdue .status-dot {
          background: var(--status-overdue);
        }

        html.dark .status-overdue .status-pill {
          color: #ffe3e3;
          background: rgba(239, 68, 68, 0.18);
          border: 1px solid rgba(239, 68, 68, 0.45);
        }

        html:not(.dark) .status-overdue .status-pill {
          color: #991b1b;
          background: #fee2e2;
          border: 1px solid rgba(239, 68, 68, 0.45);
        }

        .status-pending .status-dot {
          background: var(--status-pending);
        }

        html.dark .status-pending .status-pill {
          color: #f3e8ff;
          background: rgba(168, 85, 247, 0.18);
          border: 1px solid rgba(168, 85, 247, 0.45);
        }

        html:not(.dark) .status-pending .status-pill {
          color: #581c87;
          background: #f3e8ff;
          border: 1px solid rgba(168, 85, 247, 0.45);
        }

        html:not(.dark) .task-row:hover {
          background: rgba(0, 0, 0, 0.02);
        }

        .notes-toggle {
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          margin-top: 4px;
          transition: color 0.2s ease;
        }

        html.dark .notes-toggle {
          color: var(--notes-toggle-dark);
        }

        html:not(.dark) .notes-toggle {
          color: var(--notes-toggle-light);
        }

        html.dark .notes-toggle:hover {
          color: var(--notes-toggle-hover-dark);
        }

        html:not(.dark) .notes-toggle:hover {
          color: var(--notes-toggle-hover-light);
        }

        .notes-content {
          font-size: 14px;
          font-weight: normal;
          margin-top: 4px;
          overflow: hidden;
        }

        html.dark .notes-content {
          color: #e0e0e0;
        }

        html:not(.dark) .notes-content {
          color: #444444;
        }

        .notes-truncated {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .show-full-toggle {
          font-size: 13px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          transition: color 0.2s ease;
        }

        html.dark .show-full-toggle {
          color: var(--notes-toggle-dark);
        }

        html:not(.dark) .show-full-toggle {
          color: var(--notes-toggle-light);
        }

        html.dark .show-full-toggle:hover {
          color: var(--notes-toggle-hover-dark);
        }

        html:not(.dark) .show-full-toggle:hover {
          color: var(--notes-toggle-hover-light);
        }
      `}</style>

      {/* Show loading state */}
      {isLoadingTasks && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-10248">
          <div className={`${isDark ? 'bg-navy-800' : 'bg-white'} rounded-lg p-6`} data-id="element-10249">
            <p className={isDark ? 'text-white' : 'text-gray-800'} data-id="element-10250">
              Loading tasks...
            </p>
          </div>
        </div>}

      <div className="mx-auto" data-id="element-10251">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6" data-id="element-10252">
          <h1 className={`text-2xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'} mb-4 sm:mb-0`} data-id="element-10253">
            Assigned Tasks
          </h1>
          <button onClick={() => setIsAddTaskModalOpen(true)} className={`px-4 py-2 rounded-md border-2 transition-colors ${isDark ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/5'}`} data-id="element-10254">
            Create New Task
          </button>
        </div>

        {/* Filters row */}
        <div className="grid grid-cols-12 gap-4 mb-6" data-id="element-10255">
          <div className="col-span-12 md:col-span-8" id="filters-left" data-id="element-10256">
            <div className={`flex items-center rounded-lg px-3 ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-10257">
              <SearchIcon className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10258" />
              <input type="text" placeholder="Search tasks..." className={`w-full p-3 bg-transparent border-none focus:ring-0 focus:outline-none ${isDark ? 'text-white placeholder-gray-400' : 'text-gray-800 placeholder-gray-500'}`} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-10259" />
            </div>
          </div>
          <div className="col-span-12 md:col-span-4" id="filters-right" data-id="element-10260">
            <div className="flex items-center" data-id="element-10261">
              <select className={`w-full p-3 rounded-lg border-none focus:ring-0 ${isDark ? 'bg-navy-800 text-white' : 'bg-white text-gray-800'}`} value={statusFilter} onChange={e => {
              const newStatus = e.target.value;
              setStatusFilter(newStatus);
              setChartFilter(newStatus === 'all' ? null : newStatus);
              // NEW: Mark this as a dropdown-initiated filter
              setFilterSource(newStatus === 'all' ? null : 'dropdown');
            }} data-id="element-10262">
                <option value="all" data-id="element-10263">Active Tasks</option>
                <option value="pending" data-id="element-10264">Pending</option>
                <option value="in-progress" data-id="element-10265">In Progress</option>
                <option value="completed" data-id="element-10266">Completed</option>
                <option value="overdue" data-id="element-10267">Overdue</option>
              </select>
            </div>
          </div>
        </div>

        {/* Content row */}
        <div className="grid grid-cols-12 gap-4" data-id="element-10268">
          <div className="col-span-12 md:col-span-8" id="content-left" data-id="element-10269">
            <div className={`rounded-xl ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm p-6`} data-id="element-10270">
              <div className="flex justify-between items-center mb-4" data-id="element-10271">
                <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10272">
                  {displayTasks.length}{' '}
                  {displayTasks.length === 1 ? 'Task' : 'Tasks'}{' '}
                  {chartFilter && <span className="ml-1" data-id="element-10273">
                      (
                      {chartFilter === 'in-progress' ? 'In Progress' : chartFilter.charAt(0).toUpperCase() + chartFilter.slice(1)}
                      )
                    </span>}
                </h2>
                {/* Show active filter badge if a chart filter is applied */}
                {chartFilter && <div className="flex items-center" data-id="element-10274">
                    <span className={`px-2 py-1 rounded-lg text-xs font-medium mr-2 ${chartFilter === 'completed' ? 'bg-green-100 text-green-800' : chartFilter === 'in-progress' ? 'bg-yellow-100 text-yellow-800' : chartFilter === 'overdue' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`} data-id="element-10275">
                      Filtered by:{' '}
                      {chartFilter === 'in-progress' ? 'In Progress' : chartFilter.charAt(0).toUpperCase() + chartFilter.slice(1)}
                    </span>
                    <button onClick={resetChartFilter} className={`p-1 rounded-full ${isDark ? 'bg-navy-700 hover:bg-navy-600' : 'bg-gray-200 hover:bg-gray-300'} transition-colors`} title="Clear filter" data-id="element-10276">
                      <XIcon className="h-4 w-4" data-id="element-10277" />
                    </button>
                  </div>}
              </div>

              {displayTasks.length > 0 ? <div className="space-y-3" data-id="element-10278">
                  {displayTasks.map(task => {
                // Determine status class
                const statusClass = getTaskStatusClass(task);
                // Check if notes are expanded for this task
                const isNotesExpanded = expandedNoteIds.includes(task.id);
                // Check if notes are fully expanded for this task
                const isNotesFullyExpanded = fullyExpandedNoteIds.includes(task.id);
                // Check if task has notes
                const hasNotes = task.notes && task.notes.trim().length > 0;
                return <div key={task.id} className={`task-row ${statusClass} px-4 py-3 cursor-pointer`} onClick={() => openTaskDetails(task)} data-id="element-10279">
                        {/* Mobile Layout: Stacked Rows */}
                        <div className="flex md:hidden" data-id="element-10280">
                          {/* Task Content */}
                          <div className="flex-1 flex flex-col gap-2" data-id="element-10281">
                            {/* Row 1: Student */}
                            <div className="flex justify-between items-center" data-id="element-10282">
                              <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10283">
                                Student
                              </span>
                              <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-10284">
                                {task.studentName || 'No student assigned'}
                              </span>
                            </div>

                            {/* Row 2: Task */}
                            {/* Only show task title when notes are NOT expanded */}
                            {!isNotesExpanded && <div className="flex justify-between items-center" data-id="element-10285">
                                <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10286">
                                  Task
                                </span>
                                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'} text-right`} data-id="element-10287">
                                  {task.title || task.name}
                                </span>
                              </div>}

                            {/* Row 3: Date */}
                            <div className="flex justify-between items-center" data-id="element-10288">
                              <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10289">
                                Date
                              </span>
                              <span className={`text-sm ${getTaskDisplayStatus(task) === 'overdue' ? 'text-red-500 font-medium' : isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-10290">
                                {formatDate(task.deadline || task.dueDate)}
                              </span>
                            </div>

                            {/* Row 4: Status */}
                            <div className="flex justify-between items-center" data-id="element-10291">
                              <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10292">
                                Status
                              </span>
                              <span className="status-pill inline-block px-3 py-1 rounded-full text-xs font-medium" data-id="element-10293">
                                {(() => {
                            const displayStatus = getTaskDisplayStatus(task);
                            switch (displayStatus) {
                              case 'completed':
                                return 'Completed';
                              case 'overdue':
                                return 'Overdue';
                              case 'in-progress':
                                return 'In Progress';
                              case 'pending':
                              default:
                                return 'Pending';
                            }
                          })()}
                              </span>
                            </div>

                            {/* Notes section - below main rows */}
                            {hasNotes && <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700" data-id="element-10294">
                                {!isNotesExpanded ? <button className="notes-toggle" onClick={e => toggleNoteExpansion(e, task.id)} data-id="element-10295">
                                    Show Notes{' '}
                                    <ChevronDownIcon className="h-4 w-4 ml-1" data-id="element-10296" />
                                  </button> : <div data-id="element-10297">
                                    <div className={`notes-content ${!isNotesFullyExpanded ? 'notes-truncated' : ''}`} data-id="element-10298">
                                      {task.notes}
                                    </div>
                                    {task.notes.length > 120 && !isNotesFullyExpanded && <button className="show-full-toggle mt-1" onClick={e => toggleFullNoteExpansion(e, task.id)} data-id="element-10299">
                                          Show Full
                                        </button>}
                                    <button className="notes-toggle mt-2" onClick={e => toggleNoteExpansion(e, task.id)} data-id="element-10300">
                                      Hide Notes{' '}
                                      <ChevronUpIcon className="h-4 w-4 ml-1" data-id="element-10301" />
                                    </button>
                                  </div>}
                              </div>}

                            {/* Checkbox - moved to bottom-right */}
                            <div className="flex justify-end mt-2" data-id="element-10302">
                              <button onClick={e => handleToggleComplete(task, e)} className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-[#10B981] focus:ring-offset-2 rounded-full" aria-label={task.status === 'completed' ? 'Mark task as incomplete' : 'Mark task as complete'} data-id="element-10303">
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${task.status === 'completed' ? 'bg-[#10B981] border-[#10B981]' : isDark ? 'border-gray-500 hover:border-[#10B981] hover:bg-[#10B981]/10' : 'border-gray-400 hover:border-[#10B981] hover:bg-[#10B981]/10'}`} data-id="element-10304">
                                  {task.status === 'completed' && <CheckCircleIcon className="h-3.5 w-3.5 text-white" data-id="element-10305" />}
                                </div>
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Desktop Layout: Grid with Checkbox */}
                        <div className="hidden md:grid" style={{
                    gridTemplateColumns: '1.5fr 1.8fr 1.2fr 1.2fr 40px',
                    columnGap: '20px',
                    alignItems: 'start'
                  }} data-id="element-10306">
                          {/* Column 1: Student */}
                          <div className="task-student" data-id="element-10307">
                            <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10308">
                              Student
                            </p>
                            <p className={`task-subtitle text-sm leading-snug ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-10309">
                              {task.studentName ? <span data-id="element-10310">{task.studentName}</span> : 'No student assigned'}
                            </p>
                          </div>

                          {/* Column 2: Task */}
                          <div className="task-content" data-id="element-10311">
                            <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10312">
                              Task
                            </p>
                            {/* Only show truncated title when notes are NOT expanded */}
                            {!isNotesExpanded && <h3 className={`task-title text-sm leading-snug ${isDark ? 'text-gray-300' : 'text-gray-600'}`} title={task.title || task.name} data-id="element-10313">
                                {(task.title || task.name).length > 18 ? (task.title || task.name).substring(0, 18) + '…' : task.title || task.name}
                              </h3>}
                            {/* Notes toggle button - only show if task has notes */}
                            {hasNotes && <>
                                {!isNotesExpanded ? <button className="notes-toggle" onClick={e => toggleNoteExpansion(e, task.id)} data-id="element-10314">
                                    Show Notes{' '}
                                    <ChevronDownIcon className="h-4 w-4 ml-1" data-id="element-10315" />
                                  </button> : <div className="mt-2" data-id="element-10316">
                                    <div className={`notes-content ${!isNotesFullyExpanded ? 'notes-truncated' : ''}`} data-id="element-10317">
                                      {task.notes}
                                    </div>
                                    {/* Show Full toggle - only if notes are long enough to truncate and not fully expanded */}
                                    {task.notes.length > 120 && !isNotesFullyExpanded && <button className="show-full-toggle mt-1" onClick={e => toggleFullNoteExpansion(e, task.id)} data-id="element-10318">
                                          Show Full
                                        </button>}
                                    <button className="notes-toggle mt-2" onClick={e => toggleNoteExpansion(e, task.id)} data-id="element-10319">
                                      Hide Notes{' '}
                                      <ChevronUpIcon className="h-4 w-4 ml-1" data-id="element-10320" />
                                    </button>
                                  </div>}
                              </>}
                          </div>

                          {/* Column 3: Date */}
                          <div className="task-date" data-id="element-10321">
                            <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10322">
                              Date
                            </p>
                            <p className={`text-sm leading-snug ${getTaskDisplayStatus(task) === 'overdue' ? 'text-red-500 font-medium' : isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-10323">
                              {formatDate(task.deadline || task.dueDate)}
                            </p>
                          </div>

                          {/* Column 4: Status */}
                          <div className="task-status" data-id="element-10324">
                            <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10325">
                              Status
                            </p>
                            <span className="status-pill inline-block px-3 py-1 rounded-full text-xs font-medium" data-id="element-10326">
                              {(() => {
                          const displayStatus = getTaskDisplayStatus(task);
                          switch (displayStatus) {
                            case 'completed':
                              return 'Completed';
                            case 'overdue':
                              return 'Overdue';
                            case 'in-progress':
                              return 'In Progress';
                            case 'pending':
                            default:
                              return 'Pending';
                          }
                        })()}
                            </span>
                          </div>

                          {/* Checkbox Column - moved to bottom-right */}
                          <div className="flex items-end justify-end" data-id="element-10327">
                            <button onClick={e => handleToggleComplete(task, e)} className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-[#10B981] focus:ring-offset-2 rounded-full" aria-label={task.status === 'completed' ? 'Mark task as incomplete' : 'Mark task as complete'} data-id="element-10328">
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${task.status === 'completed' ? 'bg-[#10B981] border-[#10B981]' : isDark ? 'border-gray-500 hover:border-[#10B981] hover:bg-[#10B981]/10' : 'border-gray-400 hover:border-[#10B981] hover:bg-[#10B981]/10'}`} data-id="element-10329">
                                {task.status === 'completed' && <CheckCircleIcon className="h-3.5 w-3.5 text-white" data-id="element-10330" />}
                              </div>
                            </button>
                          </div>
                        </div>
                      </div>;
              })}

                  {/* Load More Button */}
                  {hasMore && <div className="mt-6 flex justify-center" data-id="element-10331">
                      <button onClick={loadMoreTasks} disabled={isLoadingMore} className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition-colors ${isDark ? 'bg-navy-700 hover:bg-navy-600 text-white disabled:bg-navy-800 disabled:text-gray-500' : 'bg-gray-100 hover:bg-gray-200 text-gray-800 disabled:bg-gray-50 disabled:text-gray-400'}`} data-id="element-10332">
                        {isLoadingMore ? <>
                            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" data-id="element-10333"></div>
                            Loading...
                          </> : <>Load more</>}
                      </button>
                    </div>}
                </div> : <div className={`py-12 text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10334">
                  <p className="text-lg" data-id="element-10335">No tasks found</p>
                  <p className="mt-2" data-id="element-10336">
                    {searchQuery || statusFilter !== 'all' || chartFilter ? 'Try adjusting your filters' : 'Create a new task to get started'}
                  </p>
                </div>}
            </div>
          </div>

          <div className="col-span-12 md:col-span-4" id="content-right" data-id="element-10337">
            {/* Tasks by Status Donut Chart */}
            <div className={`rounded-xl ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm p-6 mb-6`} data-id="element-10338">
              <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10339">
                Tasks by Status
              </h3>
              <div id="assigned-tasks-donut" ref={chartContainer} className="w-full max-w-full mx-auto md:w-[420px]" style={{
              height: '280px',
              margin: '0 auto'
            }} data-id="element-10340"></div>
              {/* Show All button */}
              <div className="mt-2 text-center" data-id="element-10341">
                <button onClick={resetChartFilter} className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm cursor-pointer transition-all duration-300 transform hover:-translate-y-0.5 ${isDark ? 'bg-navy-700 text-white hover:bg-navy-600 hover:text-red-300' : 'bg-gray-200 text-gray-700 hover:bg-gray-300 hover:text-red-600'}`} data-id="element-10342">
                  Show All Tasks
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Task Modal */}
      {isAddTaskModalOpen && <AddTaskModal isOpen={isAddTaskModalOpen} onClose={() => setIsAddTaskModalOpen(false)} onAddTask={handleTaskAddSuccess} darkMode={isDark} teacherId={currentUser?.uid} data-id="element-10343" />}

      {/* Task Details Panel */}
      <TaskDetailsPanel open={isTaskPanelOpen} task={selectedTask} onClose={closeTaskDetails} onEditClick={handleEditClick} onExitEditMode={handleExitEditMode} onDeleteClick={handleDeleteTask} onSaveEdit={handleSaveTask} darkMode={isDark} isEditMode={isEditMode} teacherId={currentUser?.uid} data-id="element-10344" />
    </>;
};